import React, { useState } from 'react';
import { Box, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Stack, Grid } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import axios from 'axios';

// Consistent date formatting helper
const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-ZA');
};

// Consistent currency formatting helper
const formatCurrency = (value) => {
    return `R ${parseFloat(value || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function AssetInsuranceTab({ formData, handleFieldChange, isEditingMode, user, matterId }) {
    const insurances = formData?.assetInsuranceList || [];

    // State for the currently edited/new insurance policy
    const [editingId, setEditingId] = useState(null);
    const [insuranceForm, setInsuranceForm] = useState({
        companyName: '',
        contactDetails: '',
        insuredAssetsDetails: '',
        insuredAmount: '',
        policyNumber: '',
        effectiveDate: '',
        closedDate: ''
    });

    const resetForm = () => {
        setEditingId(null);
        setInsuranceForm({
            companyName: '',
            contactDetails: '',
            insuredAssetsDetails: '',
            insuredAmount: '',
            policyNumber: '',
            effectiveDate: '',
            closedDate: ''
        });
    };

    const saveToDB = async (list) => {
        if (!matterId) return;
        try {
            await axios.put(`/api/legal-matter/${matterId}`, { assetInsuranceList: list });
        } catch (e) {
            console.error("Auto-save failed", e);
        }
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        if (name === 'insuredAmount') {
            // Allow only numbers and a single decimal point, then format with commas for display
            const numericValue = value.replace(/[^0-9.]/g, '');
            const parts = numericValue.split('.');
            if (parts[0]) {
                parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            }
            setInsuranceForm(prev => ({ ...prev, [name]: parts.join('.') }));
        } else {
            setInsuranceForm(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = () => {
        if (!insuranceForm.companyName || !insuranceForm.policyNumber) return;
        
        let updatedList = [...insurances];
        if (editingId) {
            const itemToSave = { 
                ...insuranceForm, 
                insuredAmount: String(insuranceForm.insuredAmount).replace(/,/g, ''),
            };
            updatedList = updatedList.map(item => item.id === editingId ? { ...itemToSave, id: editingId } : item);
        } else {
            updatedList.push({ ...insuranceForm, id: -Math.floor(Math.random() * 1000000) });
        }

        handleFieldChange({ 
            target: { name: 'assetInsuranceList', value: updatedList, type: 'array' } 
        });

        saveToDB(updatedList);
        resetForm();
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        // Format amount for editing
        const amount = parseFloat(item.insuredAmount || 0);
        const formattedAmount = isNaN(amount) ? '' : amount.toLocaleString('en-US', { useGrouping: true, minimumFractionDigits: 0, maximumFractionDigits: 2 }).replace(/,/g, ',');

        setInsuranceForm({ ...item, insuredAmount: formattedAmount });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this insurance record?')) {
            const updatedList = insurances.filter(item => item.id !== id);
            handleFieldChange({ target: { name: 'assetInsuranceList', value: updatedList, type: 'array' } });
            saveToDB(updatedList);
        }
    };

    const isDisabled = !isEditingMode || user?.role === 'guest';

    // Color coding logic: Greyed out if the policy has a closed date
    const getRowStyle = (closedDate) => {
        if (closedDate) return { backgroundColor: '#f5f5f5', color: 'text.secondary', opacity: 0.8 }; 
        return {};
    };

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Assets Insurance
                </Typography>
            </Stack>

            {/* 📝 CAPTURE / EDIT FORM */}
            {editingId && (
                <Paper variant="outlined" sx={{ p: 3, mb: 4, bgcolor: '#fcfcfc', borderTop: `4px solid ${editingId === 'NEW' ? '#1976d2' : '#ff9800'}` }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                        {editingId === 'NEW' ? 'Add New Insurance Policy' : 'Edit Insurance Policy'}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Insurance Company's Name" name="companyName" value={insuranceForm.companyName} onChange={handleFormChange} disabled={isDisabled} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Policy Number" name="policyNumber" value={insuranceForm.policyNumber} onChange={handleFormChange} disabled={isDisabled} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Contact Details" name="contactDetails" value={insuranceForm.contactDetails} onChange={handleFormChange} disabled={isDisabled} /></Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField 
                                fullWidth 
                                size="small" 
                                label="Insured Amount" 
                                type="text" 
                                name="insuredAmount" 
                                value={insuranceForm.insuredAmount} 
                                onChange={handleFormChange} 
                                disabled={isDisabled} 
                                InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>R</Typography> }} />
                        </Grid>
                        <Grid item xs={12}><TextField fullWidth multiline rows={2} size="small" label="Assets which are insured details" placeholder="Describe the assets covered by this policy" name="insuredAssetsDetails" value={insuranceForm.insuredAssetsDetails} onChange={handleFormChange} disabled={isDisabled} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="date" label="Effective Date" InputLabelProps={{ shrink: true }} name="effectiveDate" value={insuranceForm.effectiveDate} onChange={handleFormChange} disabled={isDisabled} inputProps={{ max: '9999-12-31' }} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="date" label="Closed Date" InputLabelProps={{ shrink: true }} name="closedDate" value={insuranceForm.closedDate} onChange={handleFormChange} disabled={isDisabled} inputProps={{ max: '9999-12-31' }} /></Grid>
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 1 }}>
                                <Button variant="outlined" onClick={resetForm} disabled={isDisabled}>Cancel</Button>
                                <Button 
                                    variant="contained" 
                                    color={editingId === 'NEW' ? "primary" : "warning"} 
                                    onClick={handleSave} 
                                    disabled={isDisabled || !insuranceForm.companyName || !insuranceForm.policyNumber}
                                    startIcon={editingId === 'NEW' && <AddCircleOutlineIcon />}
                                >
                                    {editingId === 'NEW' ? 'Add Policy' : 'Update Policy'}
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2, mt: editingId ? 4 : 0 }}>
                <Button
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => {
                        setEditingId('NEW');
                        setInsuranceForm({ companyName: '', contactDetails: '', insuredAssetsDetails: '', insuredAmount: '', policyNumber: '', effectiveDate: '', closedDate: '' });
                    }}
                    disabled={isDisabled || editingId}
                >
                    Add Policy
                </Button>
            </Stack>

            {/* 📊 SUMMARY TABLE */}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>Insurance Policies Summary</Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Company & Policy No</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Insured Assets Details</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Insured Amount</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Effective Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Closed Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {insurances.map((item) => (
                            <TableRow key={item.id} hover sx={{ ...getRowStyle(item.closedDate), cursor: 'pointer' }} onClick={() => handleEdit(item)}>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{item.companyName}</Typography>
                                    <Typography variant="caption" color="text.secondary">Pol No: {item.policyNumber}</Typography>
                                </TableCell>
                                <TableCell>{item.insuredAssetsDetails || '-'}</TableCell>
                                <TableCell align="right">{formatCurrency(item.insuredAmount)}</TableCell>
                                <TableCell>{formatDateForDisplay(item.effectiveDate)}</TableCell>
                                <TableCell>{formatDateForDisplay(item.closedDate)}</TableCell>
                                <TableCell align="center">
                                    <Stack direction="row" spacing={1} justifyContent="center">
                                        <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleEdit(item); }} disabled={isDisabled}><EditIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} disabled={isDisabled}><DeleteIcon fontSize="small" /></IconButton>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                        
                        {insurances.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary', fontStyle: 'italic' }}>
                                    No insurance policies recorded yet. Add one using the form above.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

        </Box>
    );
}